import { G, Svg } from '@svgdotjs/svg.js';
import {
  ClampJoint,
  CountertopAngleProcessingBase,
  MaterialType,
  PostformingSideEnum,
  SimpleCountertopAngle,
  SimpleCountertopOutline,
  SimpleCountertopPanel,
  SimpleCountertopSide,
  SimpleCountertopSideData,
  simpleCountertopAngles,
  simpleCountertopSides,
} from 'cloud-shared-lib';
import _, { isNil } from 'lodash';

import { Rect } from '@svgdotjs/svg.js';
import {
  addTexturePattern,
  createGroupWithElements,
  getPanelMenu,
  getSideSizeEditor,
  maskAnglesProcessing,
  renderAngleSelector,
  renderAnglesEdgeBanding,
  renderAnglesProcessing,
  renderCutouts,
  renderEdgeBanding,
  renderFastenersSets,
  renderHoles,
  renderLines,
  renderPanelSelectors,
  renderPanelsJoints,
  renderPanelsJointsSelectors,
  renderPostforming,
  renderRectSizes,
  renderSideHighlight,
  renderSideSelector,
  renderSlots,
} from '../countertop-render.utils';
import {
  Countertop,
  CountertopLinesJoint,
  CountertopMaterial,
  CountertopTypeEnum,
  RenderOptions,
} from '../countertop.interface';
import { CountertopUtils, PanelLengthChangeDirection } from '../countertop.utils';
import { SimpleCountertopLine } from './simple-countertop-line';

export interface SimpleRenderOptions extends Omit<RenderOptions, 'disabledSideSelectors' | 'highlightedSides'> {
  disabledSideSelectors?: SimpleCountertopSide[];
  highlightedSides?: SimpleCountertopSideData<string>;
}

/**
 * ╔════════════════╗\
 * ║▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒║\
 * ║▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒║\
 * ╚════════════════╝
 */
export class SimpleCountertop implements Countertop {
  readonly lines: SimpleCountertopLine[] = [];

  readonly type = CountertopTypeEnum.Simple;

  id: number;

  has3d = false;
  useClampJointForPanels = true;

  /**
   * Создает столешницу со всеми линиями и панелями. по умолчанию, ширина линий равна ширине материала
   * @param length Габаритная Длина столешницы
   * @param width Габаритная ширина столешницы
   * @param material Материал столешницы
   * @param options
   */
  constructor(
    length: number,
    width: number,
    material: CountertopMaterial,
    options?: Partial<{ lines: SimpleCountertopLine[] }>
  ) {
    CountertopUtils.checkLineWidth(width, material);

    if (!options?.lines) {
      const maxCountertopPlateMatWidth = 600;
      const lineWidth =
        material.type === MaterialType.Plate ? Math.min(material.width, maxCountertopPlateMatWidth) : material.width;

      this.lines = [new SimpleCountertopLine(length, lineWidth, material, { isVertical: false })];
    } else {
      this.lines = options.lines;
      this.lines[0].isVertical = false;
    }
  }

  get material(): CountertopMaterial {
    return this.lines[0].material;
  }
  set material(value: CountertopMaterial) {
    this.lines[0].material = value;
  }

  get length(): number {
    return this.lines[0].length;
  }
  set length(value: number) {
    this.lines[0].length = value;
  }
  get width(): number {
    return this.lines[0].width;
  }
  set width(value: number) {
    CountertopUtils.checkLineWidth(value, this.lines[0].material);

    this.lines[0].width = value;
  }

  outline: SimpleCountertopOutline = new SimpleCountertopOutline();

  count = 1;

  setLineWidth(lineId: string, width: number): void {
    if (lineId === this.lines[0].uid) this.width = width;
  }

  getAngleNeighbors(angleNumber: SimpleCountertopAngle):
    | {
        leftAngle?: SimpleCountertopAngle;
        rightAngle?: SimpleCountertopAngle;
        leftSide: SimpleCountertopSide;
        rightSide: SimpleCountertopSide;
      }
    | undefined {
    switch (angleNumber) {
      case '1':
        return { leftAngle: '4', rightAngle: '2', leftSide: '4', rightSide: '1' };
      case '2':
        return { leftAngle: '1', rightAngle: '3', leftSide: '1', rightSide: '2' };
      case '3':
        return { leftAngle: '2', rightAngle: '4', leftSide: '2', rightSide: '3' };
      case '4':
        return {
          leftAngle: '3',
          rightAngle: '1',
          leftSide: '3',
          rightSide: '4',
        };

      default:
        return;
    }
  }

  getProcessableNeighborAngles(angleNumber: SimpleCountertopAngle): {
    left?: CountertopAngleProcessingBase;
    right?: CountertopAngleProcessingBase;
  } {
    const neighbors = this.getAngleNeighbors(angleNumber);

    return {
      left: this.outline.angleProcessing[neighbors.leftAngle],
      right: this.outline.angleProcessing[neighbors.rightAngle],
    };
  }

  checkSidePostforming(sideN: SimpleCountertopSide): boolean {
    switch (sideN) {
      case '1':
        return this.material.postforming === PostformingSideEnum.TopAndBottom;

      case '3':
        return (
          this.material.postforming === PostformingSideEnum.Bottom ||
          this.material.postforming === PostformingSideEnum.TopAndBottom
        );

      default:
        return false;
    }
  }

  setPanelLength(value: number, lineUid: string, panelUid: string, direction: PanelLengthChangeDirection): void {
    const line: SimpleCountertopLine = this.lines.find(l => l.uid === lineUid);

    if (!line) {
      throw new Error(`Линия не найдена`);
    }

    const panel: SimpleCountertopPanel = line.panels.find(p => p.uid === panelUid);

    if (!panel) {
      throw new Error(`Панель не найдена`);
    }

    CountertopUtils.setPanelLength(value, panel, line, direction);
  }

  deletePanel(lineUid: string, panelUid: string): number {
    const line: SimpleCountertopLine = this.lines.find(l => l.uid === lineUid);

    if (!line) {
      throw new Error(`Линия не найдена`);
    }

    const panel: SimpleCountertopPanel = line.panels.find(p => p.uid === panelUid);

    if (!panel) {
      throw new Error(`Панель не найдена`);
    }

    return CountertopUtils.removePanel(this, line, line.panels, panel);
  }

  canSetWidth(_width: number): boolean {
    return true;
  }

  canSetLength(_length: number): boolean {
    return true;
  }

  async render(
    draw: Svg,
    scaled,
    onPanelSizeChange: (
      countertop: SimpleCountertop,
      line: SimpleCountertopLine,
      panel: SimpleCountertopPanel,
      side: 'height' | 'width',
      value: number,
      error?: string
    ) => void,
    onPanelsCountChange: (
      countertop: SimpleCountertop,
      line: SimpleCountertopLine,
      panel: SimpleCountertopPanel,
      mode: 'add' | 'delete'
    ) => void,
    onAngleSelectorClick: (angleNumber: number, angleProcessing: CountertopAngleProcessingBase) => void,
    onSideSelectorClick: (sideNumber: number) => void,
    onLinesJointSelectorClick?: (joint: CountertopLinesJoint) => void,
    onClampJointClick?: (joint: ClampJoint) => void,
    options?: SimpleRenderOptions
  ): Promise<G[]> {
    const lineGroups = renderLines(this.lines);

    const panelsJoints = this.renderPanelsJoints();

    const postforming = this.renderPostforming();

    const anglesProcessing = maskAnglesProcessing(
      createGroupWithElements(this.renderAnglesProcessing()),
      lineGroups,
      draw
    );

    const cutouts = await renderCutouts(this.outline.cutouts, this.length, this.width);

    const fastenerSets = renderFastenersSets(this.outline.fastenersSets, this.length, this.width);
    const holes = createGroupWithElements(renderHoles(this.outline.customHoles, this.length, this.width));

    const slots = this.renderSlots();

    const edgeBanding = this.renderEdgeBanding();
    const anglesEdgeBanding = this.renderAnglesEdgeBanding();

    const highlightedSides = _.isNil(options?.highlightedSides)
      ? []
      : this.renderHighlightedSides(options.highlightedSides);

    const panelSelectors = options?.panelSelectors
      ? this.renderPanelSelectors(draw, scaled, onPanelSizeChange, onPanelsCountChange, {
          panelSplit: options?.panelSplit,
          panelRemoving: options?.panelRemoving,
        })
      : [];

    if (options.panelSizes) {
      const line = this.lines.find(l => l.panels.some(p => p.uid === options.panelSizes));
      const panel = line.panels.find(p => p.uid === options.panelSizes);

      const lineGroup = panelSelectors.find(lg => lg.attr().lineId === line.uid);
      if (line) {
        this.renderPanelSizes(
          draw,
          line.uid,
          lineGroup.findOne(`[panelId="${panel.uid}"]`) as Rect,
          scaled,
          onPanelSizeChange
        ).addTo(lineGroup);
      }
    }

    const sideSelectors = options?.sideSelectors
      ? this.renderSideSelectors(onSideSelectorClick, options.disabledSideSelectors)
      : [];

    const angleSelectors = options?.angleSelectors ? this.renderAngleSelectors(onAngleSelectorClick) : [];

    const panelsJointsSelectors = options?.panelsJointsSelector
      ? this.renderPanelsJointsSelectors(onClampJointClick)
      : [];

    const renderGroups = [
      ...lineGroups,
      ...panelsJoints,
      ...slots,
      postforming,
      ...edgeBanding,
      ...highlightedSides,
      anglesProcessing,
      ...anglesEdgeBanding,
      ...cutouts,
      ...fastenerSets,
      holes,
      ...panelSelectors,
      ...sideSelectors,
      ...angleSelectors,
      ...panelsJointsSelectors,
    ];
    renderGroups.forEach(l => draw.add(l));

    return renderGroups;
  }

  async renderTextureSchema(draw: Svg): Promise<G[]> {
    const lineGroups = renderLines(this.lines);

    const topFaceBanding = _.maxBy(this.outline.faceBanding, b => b.level);
    const topMaterial = !isNil(topFaceBanding) && topFaceBanding.level > 0 ? topFaceBanding.material : this.material;

    addTexturePattern(draw, topMaterial.color ? `#${topMaterial.color}` : null, {
      ...topMaterial.textureSettings,
      texturePath: topMaterial.textureLink,
    }).then(p => {
      lineGroups.forEach(g => {
        g.children().each(e => {
          if (e.attr().type === 'line') {
            e.fill(p);
          }
        });
      });
    });

    const anglesProcessing = maskAnglesProcessing(
      createGroupWithElements(this.renderAnglesProcessing()),
      lineGroups,
      draw
    );

    const cutouts = await renderCutouts(this.outline.cutouts, this.length, this.width);

    const renderGroups = [...lineGroups, anglesProcessing, ...cutouts];
    renderGroups.forEach(l => draw.add(l));

    return renderGroups;
  }

  getLineByAngleNumber = (angleNumber: number): SimpleCountertopLine => this.lines[0];

  getSideLengthByNumber(n: string): number {
    switch (n) {
      case '1':
      case '3':
        return this.length;
      case '2':
      case '4':
        return this.width;
    }
  }

  getNeighborsByAxis(angleNumber: SimpleCountertopAngle, axis: 'x' | 'y'): { angle: string; side: string } {
    const angleNeighbors = this.getAngleNeighbors(angleNumber);

    if (axis === 'x') {
      return +angleNumber % 2 === 0
        ? { angle: angleNeighbors.leftAngle, side: angleNeighbors.leftSide }
        : { angle: angleNeighbors.rightAngle, side: angleNeighbors.rightSide };
    }

    return +angleNumber % 2 === 0
      ? { angle: angleNeighbors.rightAngle, side: angleNeighbors.rightSide }
      : { angle: angleNeighbors.leftAngle, side: angleNeighbors.leftSide };
  }

  private renderPostforming(): G {
    if (!this.material.postforming) {
      return new G();
    }

    const postforming = this.material.postforming;

    const top = createGroupWithElements([renderPostforming(this.length).attr({ postforming: '' })]);

    const isBottomPostforming = postforming === PostformingSideEnum.Bottom;

    top.y(isBottomPostforming ? this.width : 0);

    return top;
  }

  private renderAnglesProcessing(): G[] {
    const anglesProcessing = renderAnglesProcessing(this.outline.angleProcessing);
    anglesProcessing.forEach(p => {
      this.angleProcessingPositioning(p.attr().angleName.toString(), p);
    });

    return anglesProcessing;
  }

  private angleProcessingPositioning(angleName: string, group: G): G {
    switch (angleName) {
      case '1': {
        return group.center(0, 0);
      }

      case '2': {
        return group.center(this.length, 0).flip('x');
      }

      case '3': {
        return group.center(this.length, this.width).flip('both');
      }

      case '4': {
        return group.center(0, this.width).flip('y');
      }

      default:
        return group;
    }
  }

  private renderPanelsJoints(): G[] {
    const hasJoint = this.lines.some(l => l.panelsJoints.every(c => !isNil(c.connector)));

    if (!hasJoint || !this.useClampJointForPanels) return [];

    return this.lines.map(l => renderPanelsJoints(l));
  }

  private renderPanelsJointsSelectors(onClick?: (connector: ClampJoint) => void): G[] {
    const hasJoint = this.lines.some(l => l.panelsJoints.every(c => !isNil(c.connector)));

    if (!hasJoint || !this.useClampJointForPanels) return [];

    return this.lines.map(l => renderPanelsJointsSelectors(l, onClick));
  }

  private renderEdgeBanding(): G[] {
    return _(this.outline.edgeBanding)
      .map((v, k: SimpleCountertopSide) => (v ? { n: k, value: v } : null))
      .compact()
      .map(({ n, value }) => {
        const group = renderEdgeBanding(n, this.getSideLengthByNumber(n), value);
        this.sidePositioning(n, group);

        return group;
      })
      .value();
  }

  private renderAnglesEdgeBanding(): G[] {
    return _(this.outline.angleProcessing)
      .map((v, k: SimpleCountertopSide) => (v ? { n: k, value: v } : null))
      .compact()
      .filter(v => !!v.value?.parameters?.edgeBanding)
      .map(({ n, value }) => {
        const group = renderAnglesEdgeBanding(value);

        this.angleProcessingPositioning(n, group);

        return group;
      })
      .value();
  }

  private renderHighlightedSides(colors?: SimpleCountertopSideData<string>): G[] {
    return _(colors)
      .map((v, k: SimpleCountertopSide) => (v ? { n: k, value: v } : null))
      .compact()
      .map(({ n, value }) => {
        const group = createGroupWithElements([renderSideHighlight(this.getSideLengthByNumber(n), value)]);
        this.sidePositioning(n, group);

        return group;
      })
      .value();
  }

  private sidePositioning(sideN: SimpleCountertopSide, group: G): void {
    switch (sideN) {
      case '2':
        group.rotate(90, 0, 0).dy(-this.length);
        break;

      case '3':
        group.flip('y', this.width / 2).flip('x');
        break;

      case '4':
        group.rotate(-90, 0, 0).dx(-this.width);
        break;
    }
  }

  private renderSlots(): G[] {
    const slotGroups = renderSlots(this.outline.slots, this.getSideLengthByNumber.bind(this));

    slotGroups.forEach(g => {
      const sideN = g.attr().slotSide.toString();

      if (sideN === '2') {
        g.children().forEach(s =>
          s
            .rotate(-90, 0, 0)
            .x(-this.width)
            .flip('x', this.length / 2)
        );
      }

      if (sideN === '3') {
        g.children().forEach(s => s.flip('y', this.width / 2));
      }

      if (sideN === '4') {
        g.children().forEach(s => s.rotate(-90, 0, 0).x(-this.width));
      }
    });

    return slotGroups;
  }

  private renderPanelSelectors(
    draw: Svg,
    scaled: number,
    onPanelSizeChange: (
      countertop: SimpleCountertop,
      line: SimpleCountertopLine,
      panel: SimpleCountertopPanel,
      side: 'height' | 'width',
      value: number,
      error?: string
    ) => void,
    onPanelsCountChange: (
      countertop: SimpleCountertop,
      line: SimpleCountertopLine,
      panel: SimpleCountertopPanel,
      mode: 'add' | 'delete'
    ) => void,
    options?: { panelSplit?: boolean; panelRemoving?: boolean }
  ): G[] {
    const lineGroups = renderPanelSelectors(this.lines, (rect, lineGroup) => {
      const elementsToDelete = draw.find('[type="panelSizes"],[type="actions"]');
      _.invokeMap(elementsToDelete, 'remove');

      const lineId = lineGroup.attr().lineId;

      {
        const line = this.lines.find(l => l.uid === lineId);
        const panel = line?.panels.find(p => p.uid === rect.attr().panelId);
        const { panelSplit, panelRemoving } = options ?? {};

        getPanelMenu(this, line, panel, scaled, onPanelsCountChange, { panelSplit, panelRemoving })
          .center(rect.cx(), rect.cy())
          .addTo(lineGroup);
      }

      this.renderPanelSizes(draw, lineId, rect, scaled, onPanelSizeChange).addTo(lineGroup);
    });

    return lineGroups;
  }
  private renderPanelSizes(
    draw: Svg,
    lineId: string,
    rect: Rect,
    scaled: number,
    onPanelSizeChange: (
      countertop: SimpleCountertop,
      line: SimpleCountertopLine,
      panel: SimpleCountertopPanel,
      side: 'height' | 'width',
      value: number,
      error?: string
    ) => void
  ): G {
    return createGroupWithElements(
      renderRectSizes(rect, scaled, this.width, this.length, 'ltr', ['top', 'left'], (t, panelGroup) => {
        const elementsToDelete = draw.find('foreignObject[type="sizeSettings"]');
        _.invokeMap(elementsToDelete, 'remove');

        {
          const sizeSide: 'width' | 'height' = panelGroup.attr().side;

          const line = this.lines.find(l => l.uid === lineId);
          const panel = line?.panels.find(p => p.uid === rect.attr().panelId);

          const editor = getSideSizeEditor(
            this,
            line,
            panel,
            sizeSide,
            `${t.bbox().height}px`,
            t.bbox().width * 3,
            t.bbox().height * 1.5,
            +t.text(),
            onPanelSizeChange
          )
            .center(t.cx(), t.cy())
            .rotate(sizeSide === 'width' ? 90 : 0)
            .addTo(panelGroup);

          editor.node.querySelector('input')?.select();
        }
      })
    ).attr({ type: 'panelSizes' });
  }

  private renderAngleSelectors(
    onClick: (angleNumber: number, angleProcessing: CountertopAngleProcessingBase) => void
  ): G[] {
    const angleSelectors = simpleCountertopAngles.map(n =>
      renderAngleSelector(
        +n,
        this.outline.angleProcessing[n],
        Math.min(this.lines[0].width, this.lines[0].length) / 3,
        onClick
      )
    );

    angleSelectors.forEach(p => {
      this.angleProcessingPositioning(p.attr().angleName.toString(), p);
    });

    return angleSelectors;
  }

  private renderSideSelectors(onClick: (sideN: number) => void, disabledSides: SimpleCountertopSide[] = []): G[] {
    const groups = _.difference(simpleCountertopSides, disabledSides).map(n => {
      const selector = renderSideSelector(+n, this.getSideLengthByNumber(n), onClick);

      this.sidePositioning(n, selector);

      return selector;
    });

    return groups;
  }
}
